import { useEffect } from "react";
import type { UserWithRelations } from "@/lib/auth/types";

interface UseAuthStatusProps {
  setUser: (user: UserWithRelations | null) => void;
  setAccessToken: (token: string | null) => void;
  setLoading: (loading: boolean) => void;
}

export function useAuthStatus({
  setUser,
  setAccessToken,
  setLoading,
}: UseAuthStatusProps) {
  console.log('useAuthStatus: Hook initialized'); // Debug: Hook init
  
  useEffect(() => {
    console.log('useAuthStatus: Effect running'); // Debug: Effect running
    const checkAuthStatus = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        console.log('useAuthStatus: Found token:', !!token); // Debug: Token found
        
        if (!token) {
          try {
            const refreshResponse = await fetch("/api/auth/refresh", {
              method: "POST",
              credentials: "include", // This will include the refresh_token cookie
            });
    
            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json();
              console.log('useAuthStatus: Token refresh successful, setting user:', refreshData.user);
              setUser(refreshData.user);
              setAccessToken(refreshData.token);
            } else {
              console.log('useAuthStatus: Token refresh failed, no valid session');
              setUser(null);
              setAccessToken(null);
            }
          } catch (refreshError) {
            console.log('useAuthStatus: Token refresh error:', refreshError);
            setUser(null);
            setAccessToken(null);
          }
          setLoading(false);
          return;
        }

        const response = await fetch("/api/auth/validate-session", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log('useAuthStatus: Session valid, setting user:', data.user); // Debug: Valid session
          setUser(data.user);
          setAccessToken(token);
        } else {
          console.log('useAuthStatus: Session invalid, clearing auth'); // Debug: Invalid session
          localStorage.removeItem("accessToken");
          setUser(null);
          setAccessToken(null);
        }
      } catch (error) {
        console.log('useAuthStatus: Error checking auth:', error); // Debug: Auth error
        localStorage.removeItem("accessToken");
        setUser(null);
        setAccessToken(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, [setUser, setAccessToken, setLoading]);
}
